import React, { FC, useCallback } from 'react';
import { Link } from 'gatsby';
import classNames from 'classnames';
import Nav from 'react-bootstrap/Nav';

import HeaderNavDropdownItem from 'components/Header/HeaderNavDropdownItem';
import DropdownLanguage from 'components/DropdownLanguage';
import { useBV } from 'hooks/useBV';

import { isBrowser } from 'utils/browser';
import { extractUrlFromMultiPicker, getHomePageUrlPrefixes } from 'utils/urlMultiPicker';
import { IHeaderNav, IMenuItemRendererProps } from './models';
import './HeaderNav.scss';

const MenuItemRenderer: FC<IMenuItemRendererProps> = ({
  navItem,
  homePageUrlPrefixes,
  ...restProps
}) => {
  const pathname = isBrowser() ? window.location.pathname : '';
  const BV = useBV();
  const extractedUrl = extractUrlFromMultiPicker(navItem.url);
  const isGenericReview = extractedUrl.includes('#GenericReview');
  const linkClick = useCallback(() => {
    restProps.handleRedirect();
    isGenericReview && BV.showProductPicker();
  }, []);

  if (navItem.panels) {
    return <HeaderNavDropdownItem {...restProps} navItem={navItem} />;
  }

  return (
    <Link
      onFocus={() => restProps.showMenu(false)}
      to={`${isGenericReview ? '#reviews' : extractedUrl}`}
      className={classNames('nav-link', {
        active: pathname.includes(extractedUrl) && !homePageUrlPrefixes?.includes(extractedUrl),
      })}
      onClick={linkClick}
    >
      {navItem.label}
    </Link>
  );
};

const HeaderNav: FC<IHeaderNav> = ({
  menu,
  languageSelector: { title: langSelectorTitle, languages, currentLangCode, redirectBehavior },
  ...restProps
}) => {
  const navRight = menu?.navigation ? [...menu.navigation] : [];
  const navLeft = navRight.splice(0, Math.round(menu?.navigation.length / 2));
  const homePageUrlPrefixes = getHomePageUrlPrefixes(languages);

  return (
    <div className="dt-header__navbar-holder">
      <Nav>
        <div className="dt-header__nav-left">
          {navLeft.map(({ properties }) => (
            <MenuItemRenderer
              key={`HeaderNavDropdownItem-left-${properties.label}`}
              navItem={properties}
              homePageUrlPrefixes={homePageUrlPrefixes}
              {...restProps}
            />
          ))}
        </div>

        <div className="dt-header__nav-right">
          <div className="dt-header__nav-right-holder">
            {navRight.map(({ properties }) => (
              <MenuItemRenderer
                key={`HeaderNavDropdownItem-right-${properties.label}`}
                navItem={properties}
                homePageUrlPrefixes={homePageUrlPrefixes}
                {...restProps}
              />
            ))}
          </div>
        </div>
      </Nav>
      {languages ? (
        <DropdownLanguage
          title={langSelectorTitle}
          langs={languages}
          currentLangCode={currentLangCode}
          redirectToHome={redirectBehavior}
        />
      ) : null}
    </div>
  );
};

export default HeaderNav;
